/// <reference lib="webworker" />

// Whisper.cpp STT runtime. Owns a nested classic worker
// (`/whisper-worker.js`) which hosts the emscripten-compiled whisper.cpp
// WASM build, with the `tiny.en` Q5_1 ggml model. Replaces the previous
// Moonshine + Transformers.js + ORT path that bench 05/06/08 measured as
// un-shippable on iPhone Safari.
//
// Telemetry surface — preserved 1:1 with bench 09 so the integration is
// as inspectable as the bench was. Every emit goes through the supplied
// `onTelemetry` callback (which the heavy-operation-worker forwards to
// the dev harness as `worker.heartbeat`/`worker.request_*` style events,
// keeping the shape consistent with the rest of the worker telemetry):
//
//   stt.whisper.worker_boot           — nested worker first message
//   stt.whisper.runtime_initialized   — emscripten Module ready
//   stt.whisper.import_fail           — main.js importScripts threw
//   stt.whisper.model_loaded          — model bytes copied into FS
//   stt.whisper.init_done             — whisper instance created
//   stt.whisper.stdout                — every whisper.cpp print line
//   stt.whisper.stderr                — every whisper.cpp printErr line
//   stt.whisper.transcribe_start      — full_default dispatched
//   stt.whisper.transcribe_done       — completed (with finish_reason)
//   stt.whisper.error                 — protocol-level failure
//
// All timing fields are milliseconds. `finish_reason` is one of
// `end-marker` (the canonical happy path), `quiescence`, `no-output`,
// `hard-timeout` — same vocabulary as the bench worker so a future
// stt.whisper.transcribe_done event in the JSONL can be triaged by the
// same eyeball pattern.

import type { LaneManifest } from "@/runtime/events";
import { readManifestAssetFile } from "@/runtime/local-manifest-assets";

declare const self: DedicatedWorkerGlobalScope;

export type WhisperStdoutCallback = (line: string, stream: "stdout" | "stderr") => void;

export interface WhisperTelemetryEvent {
  event: string;
  level?: "debug" | "info" | "warning" | "error";
  msg?: string;
  data?: Record<string, unknown>;
}

export type WhisperOnTelemetry = (telemetry: WhisperTelemetryEvent) => void;

interface WhisperWorkerReadyMessage {
  kind: "ready" | "model-loaded" | "init-done" | "transcribe-done";
  requestId: number;
  [key: string]: unknown;
}

interface WhisperWorkerErrorMessage {
  kind: "error";
  requestId?: number;
  cmd?: string;
  error: string;
  stack?: string;
}

interface WhisperWorkerStreamMessage {
  kind: "stdout" | "stderr";
  text: string;
}

interface WhisperWorkerRuntimeMessage {
  kind:
    | "worker-boot"
    | "import-fail"
    | "runtime-initialized"
    | "model-loaded"
    | "init-done"
    | "transcribe-done"
    | "module-info";
  [key: string]: unknown;
}

type WhisperWorkerMessage =
  | WhisperWorkerReadyMessage
  | WhisperWorkerErrorMessage
  | WhisperWorkerStreamMessage
  | WhisperWorkerRuntimeMessage;

export interface WhisperTranscribeResult {
  text: string;
  ret: number;
  elapsedMs: number;
  finishReason: string | null;
  stdoutLines: number;
  stderrLines: number;
  capturedSegments: string[];
  capturedAll: string[];
}

interface WhisperRuntimeState {
  worker: Worker;
  workerReady: Promise<void>;
  modelLoaded: boolean;
  modelManifestId: string | null;
  instance: number;
  nextRequestId: number;
  pendingByRequestId: Map<
    number,
    { resolve: (msg: WhisperWorkerReadyMessage) => void; reject: (err: Error) => void }
  >;
  // The transcribe pipeline accumulates stdout lines so we can produce a
  // useful `text` field without leaking text-vs-progress lines to the
  // caller. The whisper.cpp output format for English is segments like
  // `[hh:mm:ss.xxx --> hh:mm:ss.xxx]   text`; we strip the timestamps and
  // join. Segment lines arrive on stdout; everything else (load logs,
  // timings) is on stderr or non-segment stdout.
  capturedAll: string[];
  capturedSegments: string[];
  onTelemetry: WhisperOnTelemetry;
}

let runtime: WhisperRuntimeState | null = null;
let runtimeManifestId: string | null = null;

function noopTelemetry(_t: WhisperTelemetryEvent): void {
  /* default sink */
}

function emit(event: string, data?: Record<string, unknown>, level?: WhisperTelemetryEvent["level"]): void {
  runtime?.onTelemetry({ event, level, data });
}

const SEGMENT_LINE_RE = /^\s*\[\d{2}:\d{2}:\d{2}\.\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}\.\d{3}\]\s*(.*)$/;

function tryExtractSegmentText(line: string): string | null {
  const match = SEGMENT_LINE_RE.exec(line);
  return match ? match[1].trim() : null;
}

function ensureWorker(onTelemetry: WhisperOnTelemetry): WhisperRuntimeState {
  if (runtime) {
    runtime.onTelemetry = onTelemetry;
    return runtime;
  }
  // The product worker URL is fixed and root-served. We never want this
  // to be bundled by Vite (it must be served verbatim alongside the
  // emscripten main.js it imports), so we use an absolute path. Same
  // trick the bench uses.
  const worker = new Worker("/whisper-worker.js");
  let workerReadyResolve: () => void = () => undefined;
  let workerReadyReject: (err: Error) => void = () => undefined;
  const workerReady = new Promise<void>((resolve, reject) => {
    workerReadyResolve = resolve;
    workerReadyReject = reject;
  });
  const state: WhisperRuntimeState = {
    worker,
    workerReady,
    modelLoaded: false,
    modelManifestId: null,
    instance: 0,
    nextRequestId: 0,
    pendingByRequestId: new Map(),
    capturedAll: [],
    capturedSegments: [],
    onTelemetry,
  };
  runtime = state;

  worker.onmessage = (event: MessageEvent<WhisperWorkerMessage>) => {
    const msg = event.data;
    if (!msg || typeof msg !== "object") return;
    if (msg.kind === "worker-boot") {
      const at = (msg as WhisperWorkerRuntimeMessage).at as number | undefined;
      emit("stt.whisper.worker_boot", { at_wall_ms: at ?? null });
      return;
    }
    if (msg.kind === "import-fail") {
      const error = (msg as { error?: string }).error;
      emit("stt.whisper.import_fail", { error: error ?? "unknown" }, "error");
      workerReadyReject(new Error(`whisper worker import failed: ${error}`));
      return;
    }
    if (msg.kind === "runtime-initialized") {
      emit("stt.whisper.runtime_initialized", {});
      workerReadyResolve();
      return;
    }
    if (msg.kind === "stdout") {
      const text = (msg as WhisperWorkerStreamMessage).text ?? "";
      state.capturedAll.push(text);
      const segText = tryExtractSegmentText(text);
      if (segText) state.capturedSegments.push(segText);
      emit("stt.whisper.stdout", { text }, "debug");
      return;
    }
    if (msg.kind === "stderr") {
      const text = (msg as WhisperWorkerStreamMessage).text ?? "";
      state.capturedAll.push("ERR: " + text);
      emit("stt.whisper.stderr", { text }, "debug");
      return;
    }
    if (msg.kind === "error") {
      const err = msg as WhisperWorkerErrorMessage;
      const requestId = err.requestId;
      emit(
        "stt.whisper.error",
        { request_id: requestId ?? null, cmd: err.cmd ?? null, error: err.error, stack: err.stack ?? null },
        "warning",
      );
      if (typeof requestId === "number") {
        const pending = state.pendingByRequestId.get(requestId);
        if (pending) {
          state.pendingByRequestId.delete(requestId);
          pending.reject(new Error(err.error));
        }
      }
      return;
    }
    // Generic ready-style messages (model-loaded, init-done,
    // transcribe-done) include a requestId.
    const ready = msg as WhisperWorkerReadyMessage;
    if (typeof ready.requestId === "number") {
      const pending = state.pendingByRequestId.get(ready.requestId);
      if (pending) {
        state.pendingByRequestId.delete(ready.requestId);
        pending.resolve(ready);
      }
    }
  };

  worker.onerror = (event) => {
    emit(
      "stt.whisper.worker_error",
      {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
      },
      "error",
    );
  };

  return state;
}

function callWorker(
  state: WhisperRuntimeState,
  cmd: string,
  args: Record<string, unknown> = {},
  transferList: Transferable[] = [],
  timeoutMs = 180_000,
): Promise<WhisperWorkerReadyMessage> {
  return new Promise((resolve, reject) => {
    const requestId = ++state.nextRequestId;
    state.pendingByRequestId.set(requestId, { resolve, reject });
    state.worker.postMessage({ cmd, requestId, ...args }, transferList);
    setTimeout(() => {
      if (state.pendingByRequestId.has(requestId)) {
        state.pendingByRequestId.delete(requestId);
        reject(new Error(`whisper worker ${cmd} timed out after ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });
}

async function readModelBytesFromOpfs(manifest: LaneManifest): Promise<{ bytes: ArrayBuffer; sourcePath: string; sourceBytes: number }> {
  const t0 = performance.now();
  const file = await readManifestAssetFile(manifest, "ggml-tiny.en-q5_1.bin");
  const bytes = await file.arrayBuffer();
  emit("stt.whisper.model_read_from_opfs", {
    manifest_id: manifest.id,
    bytes: bytes.byteLength,
    elapsed_ms: Math.round(performance.now() - t0),
  });
  return { bytes, sourcePath: file.name ?? "ggml-tiny.en-q5_1.bin", sourceBytes: bytes.byteLength };
}

export async function ensureWhisperReady(
  manifest: LaneManifest,
  onTelemetry: WhisperOnTelemetry = noopTelemetry,
): Promise<void> {
  const state = ensureWorker(onTelemetry);
  await state.workerReady;
  if (state.modelLoaded && state.modelManifestId === manifest.id && state.instance) {
    return;
  }
  if (runtimeManifestId !== manifest.id) {
    state.modelLoaded = false;
    state.instance = 0;
  }
  if (!state.modelLoaded) {
    const t0 = performance.now();
    const { bytes } = await readModelBytesFromOpfs(manifest);
    const loadResult = await callWorker(state, "load-model", { bytes, fname: "whisper.bin" }, [bytes]);
    state.modelLoaded = true;
    emit("stt.whisper.model_loaded", {
      manifest_id: manifest.id,
      bytes: loadResult.bytes ?? null,
      elapsed_ms: loadResult.elapsed_ms ?? null,
      total_setup_ms: Math.round(performance.now() - t0),
    });
  }
  if (!state.instance) {
    const initResult = await callWorker(state, "init", { fname: "whisper.bin" });
    state.instance = (initResult.instance as number) ?? 0;
    emit("stt.whisper.init_done", {
      manifest_id: manifest.id,
      instance: state.instance,
      elapsed_ms: initResult.elapsed_ms ?? null,
    });
  }
  state.modelManifestId = manifest.id;
  runtimeManifestId = manifest.id;
}

export interface SttTranscriptionResult {
  durationMs: number;
  text: string;
}

export async function transcribeWithStt(
  manifest: LaneManifest,
  audio16k: Float32Array,
  options: { onTelemetry?: WhisperOnTelemetry } = {},
): Promise<SttTranscriptionResult> {
  const onTelemetry = options.onTelemetry ?? noopTelemetry;
  await ensureWhisperReady(manifest, onTelemetry);
  const state = runtime;
  if (!state) throw new Error("whisper runtime not initialized");
  state.capturedAll = [];
  state.capturedSegments = [];
  emit("stt.whisper.transcribe_start", {
    manifest_id: manifest.id,
    samples: audio16k.length,
    duration_ms: Math.round((audio16k.length / 16) /* 16 samples per ms at 16 kHz */),
  });
  const startedAt = performance.now();
  // Transfer the audio buffer into the worker so we don't double the heap
  // briefly. The page won't read audio16k after this.
  const transcribeResult = await callWorker(
    state,
    "transcribe",
    { audio16k, lang: "en", nthreads: 1, translate: false },
    [audio16k.buffer],
  );
  const elapsedMs = Math.round(performance.now() - startedAt);
  const text = state.capturedSegments.join(" ").trim();
  const ret = (transcribeResult.ret as number | undefined) ?? -1;
  const finishReason = (transcribeResult.finish_reason as string | undefined) ?? null;
  const stdoutLines = (transcribeResult.stdout_lines as number | undefined) ?? state.capturedAll.length;
  const stderrLines = (transcribeResult.stderr_lines as number | undefined) ?? 0;
  emit("stt.whisper.transcribe_done", {
    manifest_id: manifest.id,
    ret,
    elapsed_ms: elapsedMs,
    finish_reason: finishReason,
    transcript_chars: text.length,
    segment_count: state.capturedSegments.length,
    stdout_lines: stdoutLines,
    stderr_lines: stderrLines,
    samples: audio16k.length, // populated from the source value at start; transferred buffer would read 0
  });
  if (ret !== 0 || !text) {
    throw new Error(
      `whisper transcribe returned ret=${ret}, finish_reason=${finishReason}, text_chars=${text.length}`,
    );
  }
  return { durationMs: elapsedMs, text };
}

export interface WhisperFixtureSmokeResult {
  durationMs: number;
  text: string;
  finishReason: string | null;
  stdoutLines: number;
  stderrLines: number;
  segmentCount: number;
}

// For the lane smoke probe: run the same transcribe on a caller-provided
// Float32Array (typically a fixture WAV) and return both the transcript
// and the rich diagnostics. The smoke check uses this to assert "real
// model is loadable + a known input produces non-empty text".
export async function smokeWhisperTranscribe(
  manifest: LaneManifest,
  audio16k: Float32Array,
  options: { onTelemetry?: WhisperOnTelemetry } = {},
): Promise<WhisperFixtureSmokeResult> {
  const onTelemetry = options.onTelemetry ?? noopTelemetry;
  await ensureWhisperReady(manifest, onTelemetry);
  const state = runtime;
  if (!state) throw new Error("whisper runtime not initialized");
  state.capturedAll = [];
  state.capturedSegments = [];
  emit("stt.whisper.smoke_start", {
    manifest_id: manifest.id,
    samples: audio16k.length,
  });
  const startedAt = performance.now();
  const result = await callWorker(
    state,
    "transcribe",
    { audio16k, lang: "en", nthreads: 1, translate: false },
    [audio16k.buffer],
  );
  const elapsedMs = Math.round(performance.now() - startedAt);
  const text = state.capturedSegments.join(" ").trim();
  const finishReason = (result.finish_reason as string | undefined) ?? null;
  const stdoutLines = (result.stdout_lines as number | undefined) ?? state.capturedAll.length;
  const stderrLines = (result.stderr_lines as number | undefined) ?? 0;
  const ret = (result.ret as number | undefined) ?? -1;
  emit("stt.whisper.smoke_done", {
    manifest_id: manifest.id,
    ret,
    elapsed_ms: elapsedMs,
    finish_reason: finishReason,
    transcript_chars: text.length,
    segment_count: state.capturedSegments.length,
    stdout_lines: stdoutLines,
    stderr_lines: stderrLines,
  });
  if (ret !== 0 || !text) {
    throw new Error(
      `whisper smoke returned ret=${ret}, finish_reason=${finishReason}, text_chars=${text.length}`,
    );
  }
  return {
    durationMs: elapsedMs,
    text,
    finishReason,
    stdoutLines,
    stderrLines,
    segmentCount: state.capturedSegments.length,
  };
}
